/**
 * `useVisitorPresence` — anonymous visitor-presence heartbeat (live-visitors panel).
 *
 * While the widget is loaded on a host page (even with the launcher closed), beat the public
 * presence endpoint every ~25s so the org's dashboard can see who is browsing right now, on
 * which page. The visitor id is a client-random token persisted in localStorage — it carries
 * no identity; the dashboard links it to a conversation only via the optional sessionId.
 *
 * Fire-and-forget: failures are swallowed (presence must never affect the host page). Beats
 * continue while the tab is backgrounded — like the old cockpit, "active" means the site is
 * open, not focused; the 90s server TTL reaps visitors who actually leave.
 */

import { useEffect } from 'react';
import { getWidgetApi } from '../config/loadWidgetConfig';
import type { EmbedContext } from '../config/parseEmbed';

const VISITOR_ID_KEY = 'lc.visitor.id';
/** Pre-rename key, read-only: preserves a returning visitor's stable id across the rename. */
const LEGACY_VISITOR_ID_KEY = 'flozic.visitor.id';
const BEAT_MS = 15_000;

/** Stable per-browser visitor id (created once, [A-Za-z0-9_-], 8..64 chars). */
export function getVisitorId(): string {
  try {
    const existing =
      localStorage.getItem(VISITOR_ID_KEY) ?? localStorage.getItem(LEGACY_VISITOR_ID_KEY);
    if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing;
    const fresh =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `v${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(VISITOR_ID_KEY, fresh);
    return fresh;
  } catch {
    // Storage unavailable (rare) — a per-load id still shows the visitor while they browse.
    return `v${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function useVisitorPresence(
  embed: EmbedContext | null,
  sessionId: string | null,
  onEngaged?: (sessionId: string, sessionToken: string) => void,
): void {
  useEffect(() => {
    if (!embed?.cid) return;
    const visitorId = getVisitorId();
    let engagedHandled = false;

    const beat = () => {
      void getWidgetApi()
        .widget.visitorHeartbeat(embed.cid, {
          visitorId,
          pageUrl: embed.pageUrl ?? undefined,
          language:
            typeof navigator !== 'undefined' && navigator.language
              ? navigator.language
              : undefined,
          sessionId: sessionId ?? undefined,
        })
        .then((ack) => {
          // Agent-initiated chat: adopt the session once (the effect re-runs with the
          // new sessionId afterwards, so later beats carry it and stop the handoff).
          if (!engagedHandled && ack.engagedSessionId && ack.sessionToken && !sessionId) {
            engagedHandled = true;
            onEngaged?.(ack.engagedSessionId, ack.sessionToken);
          }
        })
        .catch(() => {
          /* fire-and-forget — presence never surfaces errors */
        });
    };

    beat();
    const timer = setInterval(beat, BEAT_MS);
    return () => {
      clearInterval(timer);
    };
  }, [embed?.cid, embed?.pageUrl, sessionId]);
}
